<template>
	<div class="create-backend-keywords">
		<h2>Create bulk keywords</h2>

		<div class="keywords">
			<textarea v-for="n in internalKeywords.length"
					  :key="`keyword-${n}`"
					  class="form-control"
					  :class="{ occupied: isOccupied(internalKeywords[n - 1]) }"
					  placeholder="Type a new keyword"
					  :value="internalKeywords[n - 1]"
					  @keyup="$event => onUpdate(n - 1, $event.target.value)"
			/>
		</div>

		<h3>Location</h3>

		<multiselect v-model="selectedLocations"
					 v-scrollbar
					 :customLabel="locationLabel"
					 track-by="id"
					 :placeholder="$t('seo.createKeywords.chooseLocation')"
					 open-direction="bottom"
					 :options="locations"
					 :searchable="true"
					 selectedLabel=""
					 :loading="isLoadingLocations"
					 :internal-search="false"
					 :clear-on-select="false"
					 :close-on-select="false"
					 :multiple="true"
					 :max-height="300"
					 :show-no-results="false"
					 :allow-empty="false"
					 :deselect-label="$t('seo.createKeywords.mustChooseLocation')"
					 :select-label="$t('seo.createKeywords.chooseLocation')"
					 @search-change="searchLocations"
					 @open="onDropdownOpen">

			<template slot="option" slot-scope="props">
				<span class="badge__name">{{locationLabel(props.option)}}</span>
				<span class="badge__type">{{$t('seo.createKeywords.locationType.' + props.option.type.toLowerCase())}}</span>
			</template>

			<template slot="clear" slot-scope="props">
				<div class="multiselect__clear" v-if="selectedLocations" @mousedown.prevent.stop="clearLocation()"></div>
			</template>

			<template slot="noResult">
                    <span v-if="isLoadingLocations">
                        {{$t('seo.createKeywords.loadingLocations')}}
                        <loader :inline="true" />
                    </span>

				<span v-else>{{$t('seo.createKeywords.noLocationsFound')}}</span>
			</template>
		</multiselect>

		<button class="btn"
				@click="onSubmit"
				:disabled="! canSubmit"
				:style="organizationColor" >
			<template v-if="!isSaving">
				{{$t('seo.createKeywords.createSelected')}}
			</template>

			<template v-else>
				<span class="saving-text">
					{{$t('seo.createKeywords.saving')}}
					<span class="ellipsis-animation">...</span>
				</span>
			</template>
		</button>
	</div>
</template>

<style lang="scss" scoped>
.create-backend-keywords {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;

	> div.keywords {
		display: flex;
		flex-direction: column;
		gap: 5px;

		> textarea {
			width: 300px;
			height: 40px;
			max-width: calc(100vw - 30px);

			&.occupied {
				color: #c03131;
				border-color: #c03131;
			}
		}
	}

	h2 {
		color: #000;
		font-size: 18px;
		font-weight: 500;
		margin: 0;
		padding: 0;
	}

	h3 {
		color: #343434;
		font-size: 14px;
		font-weight: 500;
		margin: 0;
		padding: 0;
	}

	.saving-text {
		display: inline-flex;
		align-items: center;
	}

	.ellipsis-animation {
		display: inline-block;
		animation: ellipsis 1.5s infinite;
		width: 1.5em;
		text-align: left;
	}

	@keyframes ellipsis {
		0% { content: '.'; }
		33% { content: '..'; }
		66% { content: '...'; }
	}
}

</style>

<!-- Global styles for multiselect component -->
<style>
.multiselect__content-wrapper::-webkit-scrollbar {
	width: 16px;
}

.multiselect__content-wrapper::-webkit-scrollbar-track {
	background: #f1f1f1;
}

.multiselect__content-wrapper::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 8px;
	border: 3px solid #f1f1f1;
}

.multiselect__content-wrapper::-webkit-scrollbar-thumb:hover {
	background: #555;
}

.multiselect__content-wrapper {
	user-select: none;
}
</style>

<script>
const LocationService = require('@/services/seo/LocationService')
const KeywordService = require('@/services/seo/KeywordService')

const scrollbarDirective = {
	inserted(el) {
		const multiselect = el;
		if (!multiselect) return;

		let isScrolling = false;
		let isInitialized = false;

		const handleDropdownOpen = () => {
			if (isInitialized) return;
			isInitialized = true;

			const contentWrapper = multiselect.querySelector('.multiselect__content-wrapper');
			if (!contentWrapper) {
				isInitialized = false;
				return;
			}

			contentWrapper.removeEventListener('mousedown', handleMouseDown);
			contentWrapper.addEventListener('mousedown', handleMouseDown);

			contentWrapper.addEventListener('scroll', () => {
				isScrolling = true;
				clearTimeout(window.scrollTimeout);
				window.scrollTimeout = setTimeout(() => {
					isScrolling = false;
				}, 200);
			});

			contentWrapper.addEventListener('click', (event) => {
				const rect = contentWrapper.getBoundingClientRect();
				const scrollbarWidth = 16;

				if (event.clientX > rect.right - scrollbarWidth) {
					event.stopPropagation();
				}
			}, true);

			const resetInitialization = () => {
				if (!multiselect.classList.contains('multiselect--active')) {
					isInitialized = false;
				}
			};

			document.addEventListener('click', resetInitialization);
		};

		const handleMouseDown = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const scrollbarWidth = 16;

			if (event.clientX > rect.right - scrollbarWidth) {
				event.stopPropagation();
				event.preventDefault();

				isScrolling = true;

				const contentWrapper = event.currentTarget;
				const startY = event.clientY;
				const startScrollTop = contentWrapper.scrollTop;

				const handleMouseMove = (moveEvent) => {
					const deltaY = moveEvent.clientY - startY;
					contentWrapper.scrollTop = startScrollTop + deltaY * 1.5;
					moveEvent.preventDefault();
				};

				const handleMouseUp = () => {
					document.removeEventListener('mousemove', handleMouseMove);
					document.removeEventListener('mouseup', handleMouseUp);

					setTimeout(() => {
						isScrolling = false;
					}, 200);
				};

				document.addEventListener('mousemove', handleMouseMove);
				document.addEventListener('mouseup', handleMouseUp);
			}
		};

		setTimeout(() => {
			const toggle = multiselect.querySelector('.multiselect__select');
			if (toggle) {
				toggle.addEventListener('click', () => {
					setTimeout(handleDropdownOpen, 50);
				});
			}
		}, 100);

		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
					if (multiselect.classList.contains('multiselect--active')) {
						setTimeout(handleDropdownOpen, 50);
					}
				}
			}
		});

		observer.observe(multiselect, { attributes: true });
	}
};

export default {
	directives: {
		scrollbar: scrollbarDirective
	},
	props: {
		keywords: {
			type: Array,
			required: true
		},
		customerId: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			selectedLocations: [],
			locations: [],
			isLoadingLocations: false,
			isSaving: false,
			internalKeywords: [''],
		}
	},

	created() {
		this.loadDefaultLocations();
	},

	computed: {
		canSubmit() {
			return this.nonOccupiedKeywords.length > 0 && this.selectedLocations.length > 0 && !this.isSaving;
		},

		organizationColor() {
			return {
				color: $org('colors.standard.button.outlined'),
				border: `1px solid ${$org('colors.standard.button.outlined')}`
			}
		},

		nonOccupiedKeywords() {
			return this.internalKeywords.slice(0, -1).filter(keyword => ! this.isOccupied(keyword))
		}
	},

	methods: {
		async onSubmit() {
			if (!this.canSubmit) {
				return;
			}

			this.isSaving = true;

			try {
				for (const keyword of this.nonOccupiedKeywords) {
					for (const location of this.selectedLocations) {
						const response = await new Promise((resolve, reject) => {
							KeywordService.create({
								keyword,
								location: location.id,
								customerId: this.customerId
							}, resolve, reject);
						});

						eventHub.$emit('keyword.created', response.data);
					}
				}

				this.clearForm();
			} catch (error) {
				console.error('Failed to create keywords:', error);
			} finally {
				this.isSaving = false;
			}
		},

		isOccupied(keyword) {
			return this.keywords.find(item => String(item.keyword).toLocaleLowerCase() === String(keyword).toLocaleLowerCase())
		},

		onUpdate(index, value) {
			const parts = value.split('\n')

			let copy = JSON.parse(JSON.stringify(this.internalKeywords))
			copy[index] = parts[0]

			if (parts.length > 1) {
				copy.splice(index + 1, 0, ...parts.slice(1))
			}

			this.internalKeywords = [
				...copy.filter(item => String(item || '').length > 0),
				''
			]
		},

		searchLocations(query) {
			this.cachedQuery = query

			if (!query || query.length === 0) {
				if (!this.locationsLoaded) {
					this.loadDefaultLocations();
				}
				return;
			}

			if (query.length < 3) {
				this.isLoadingLocations = false
				return
			}

			this.isLoadingLocations = true;

			(queryParameter => {
				setTimeout(() => {
					if (queryParameter !== this.cachedQuery) {
						return;
					}

					const filters = []

					query.split(' ').forEach(queryWord => {
						filters.push({
							key: 'nameCanonical',
							operator: 'ct',
							value: queryWord
						})
					})

					this.locations = []

					LocationService.findAll({
						limit: 3,

						filter_groups: [
							{
								filters: filters
							},
							{
								filters: [
									{
										key: 'type',
										operator: 'eq',
										value: 'Country'
									}
								]
							}
						]
					}, (response) => {
						const data = response.data

						let locations = data.rows || []

						LocationService.findAll({
							limit: 7,

							filter_groups: [
								{
									filters: filters
								},
								{
									filters: [
										{
											key: 'type',
											operator: 'eq',
											value: 'Country',
											not: true
										}
									]
								}
							]
						}, (response) => {
							const data = response.data
							const nonCountryLocations = data.rows || []

							const existingIds = new Set(locations.map(loc => loc.id))

							nonCountryLocations.forEach(location => {
								if (!existingIds.has(location.id)) {
									locations.push(location)
								}
							})

							locations.sort((a, b) => a.name.localeCompare(b.name));

							this.locations = locations
							this.isLoadingLocations = false
							this.locationsLoaded = true
						})
					})
				}, 800)
			})(query)
		},

		clearForm() {
			this.selectedLocations = [];
			this.internalKeywords = [''];
		},

		clearLocation() {
			this.selectedLocations = [];
		},

		loadDefaultLocations() {
			this.isLoadingLocations = true;
			this.locations = [];

			LocationService.findAll({
				limit: 3,
				filter_groups: [{ filters: [{ key: 'type', operator: 'eq', value: 'Country' }] }]
			}, (response) => {
				const countries = response.data.rows || [];

				LocationService.findAll({
					limit: 7,
					filter_groups: [{ filters: [{ key: 'type', operator: 'eq', value: 'Country', not: true }] }]
				}, (response) => {
					const nonCountries = response.data.rows || [];

					const existingIds = new Set(countries.map(loc => loc.id));

					nonCountries.forEach(location => {
						if (!existingIds.has(location.id)) {
							countries.push(location);
						}
					});

					this.locations = countries;
					this.locations.sort((a, b) => a.name.localeCompare(b.name));

					this.isLoadingLocations = false;
					this.locationsLoaded = true;
				});
			}, (error) => {
				LocationService.findAll({
					limit: 10,
					filter_groups: [{ filters: [{ key: 'type', operator: 'eq', value: 'Country', not: true }] }]
				}, (response) => {
					this.locations = response.data.rows || [];
					this.locations.sort((a, b) => a.name.localeCompare(b.name));

					this.isLoadingLocations = false;
					this.locationsLoaded = true;
				}, () => {
					this.isLoadingLocations = false;
					this.locationsLoaded = true;
				});
			});
		},

		locationLabel(location) {
			return `${location.name} (${location.nameCanonical})`
		},

		onDropdownOpen() {
			if (this.locations.length === 0 && !this.isLoadingLocations && !this.locationsLoaded) {
				this.loadDefaultLocations();
			}
		}
	}
}
</script>
